import React from 'react'
import ServicePage, { SplitSection, StackRibbon } from '../ServicePage'

const AppHero = () => (
  <svg viewBox="0 0 460 460" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="app-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#0066ff" />
        <stop offset="100%" stopColor="#00d4ff" />
      </linearGradient>
      <radialGradient id="app-glow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#00d4ff" stopOpacity="0.45" />
        <stop offset="100%" stopColor="#00d4ff" stopOpacity="0" />
      </radialGradient>
    </defs>
    <circle cx="230" cy="230" r="200" fill="url(#app-glow)">
      <animate attributeName="r" values="200;215;200" dur="6s" repeatCount="indefinite" />
    </circle>

    {/* device frame */}
    <g>
      <rect x="100" y="80" width="260" height="280" rx="22" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.18)" />
      <rect x="120" y="105" width="80" height="14" rx="4" fill="rgba(0,212,255,0.55)" />
      <rect x="120" y="130" width="220" height="10" rx="3" fill="rgba(255,255,255,0.20)" />
      <rect x="120" y="148" width="180" height="10" rx="3" fill="rgba(255,255,255,0.14)" />
      <rect x="120" y="178" width="100" height="60" rx="10" fill="url(#app-grad)" opacity="0.85">
        <animate attributeName="opacity" values="0.7;1;0.7" dur="3s" repeatCount="indefinite" />
      </rect>
      <rect x="232" y="178" width="108" height="28" rx="6" fill="rgba(255,255,255,0.10)" />
      <rect x="232" y="212" width="108" height="26" rx="6" fill="rgba(255,255,255,0.10)" />
      <rect x="120" y="252" width="220" height="12" rx="3" fill="rgba(255,255,255,0.10)" />
      <rect x="120" y="272" width="220" height="12" rx="3" fill="rgba(255,255,255,0.10)" />
      <rect x="120" y="292" width="220" height="12" rx="3" fill="rgba(255,255,255,0.10)" />
      <rect x="120" y="320" width="120" height="22" rx="11" fill="url(#app-grad)" />
    </g>

    {/* floating tag */}
    <g>
      <rect x="320" y="50" width="100" height="34" rx="17" fill="rgba(0,212,255,0.10)" stroke="rgba(0,212,255,0.5)" />
      <text x="370" y="72" textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="12" fill="#00d4ff">v1.0 RELEASE</text>
      <animateTransform attributeName="transform" type="translate" values="0 0;0 -8;0 0" dur="5s" repeatCount="indefinite" />
    </g>
    {/* floating dot */}
    <g>
      <circle cx="60" cy="120" r="8" fill="#0066ff">
        <animate attributeName="cy" values="120;108;120" dur="4s" repeatCount="indefinite" />
      </circle>
    </g>
  </svg>
)

const AppDevelopment = () => (
  <ServicePage
    slug="app-development"
    seo={{
      title: 'App Development Services',
      description:
        'Full-stack app development services from QA Tech — strategy, design, web and mobile engineering, and end-to-end testing for ambitious teams across the UK and worldwide.',
      keywords:
        'app development, software development, full-stack development, custom software, application development services, react development, node development, mobile app development, web app development, UK app developers',
    }}
    hero={{
      eyebrow: 'Service / App Development',
      title: 'Apps that ship — and keep shipping.',
      lede:
        'From early-stage MVPs to enterprise platforms, we design and build modern applications that are fast, accessible, scalable and tested end to end.',
    }}
    visual={<AppHero />}
  >
    <SplitSection
      index={1}
      align="left"
      kicker="Strategy & Discovery"
      title="We start with the outcome, not the tech."
      accent={<span className="sp-accent-code">{'// scope > spec > ship'}</span>}
    >
      <p>
        Most projects fail because the wrong thing got built &mdash; not because
        the code was wrong. We open every engagement with a focused discovery
        sprint: business goals, user journeys, success metrics, technical
        constraints. By the end you have a clear product vision, a phased
        delivery plan and a fixed view of what's worth building first.
      </p>
      <ul>
        <li>Goal mapping &amp; prioritised feature backlog</li>
        <li>User journeys &amp; lightweight prototypes</li>
        <li>Architecture options with cost &amp; risk trade-offs</li>
      </ul>
    </SplitSection>

    <SplitSection
      index={2}
      align="right"
      kicker="Design & UX"
      title="Interfaces people actually want to use."
      accent={
        <span className="sp-accent-stat">
          <span className="sp-accent-stat__num">A11y</span>
          <span className="sp-accent-stat__label">WCAG 2.2 AA</span>
        </span>
      }
    >
      <p>
        Our designers and engineers work in the same room. Wireframes turn into
        production components in days, not weeks &mdash; and accessibility,
        responsiveness and performance are baked in from the first commit, not
        bolted on at launch.
      </p>
      <p>
        Every interaction is built against a real component library so the
        product stays consistent as it grows.
      </p>
    </SplitSection>

    <SplitSection
      index={3}
      align="left"
      kicker="Engineering"
      title="Production-grade from day one."
      accent={
        <div className="sp-accent-bars" aria-hidden="true">
          <span /><span /><span /><span /><span />
        </div>
      }
    >
      <p>
        We build with TypeScript-first React, Node and modern cloud platforms.
        Code is reviewed, typed, tested and observable. CI/CD is set up on day
        one, not month six &mdash; so you can deploy on a Friday afternoon and
        sleep well.
      </p>
      <ul>
        <li><strong>Frontend:</strong> React, Next.js, TypeScript, Tailwind</li>
        <li><strong>Backend:</strong> Node.js, NestJS, Python, Go</li>
        <li><strong>Data:</strong> PostgreSQL, MongoDB, Redis, Elasticsearch</li>
        <li><strong>Cloud:</strong> AWS, GCP, Azure, Vercel, Cloudflare</li>
      </ul>
    </SplitSection>

    <SplitSection
      index={4}
      align="right"
      kicker="Quality Built-in"
      title="QA isn't a phase. It's the whole point."
    >
      <p>
        QA Tech was founded by quality engineers, so testing isn&rsquo;t the
        thing we tack on at the end &mdash; it&rsquo;s the spine of the
        delivery. Unit, integration, contract, end-to-end, performance and
        accessibility checks all run in CI on every change.
      </p>
      <p>
        That means fewer regressions, faster reviews, and a real confidence
        score behind every release.
      </p>
    </SplitSection>

    <SplitSection
      index={5}
      align="left"
      kicker="Launch & Beyond"
      title="We don't disappear after go-live."
    >
      <p>
        Launch is the start, not the finish. We stay engaged with monitoring,
        SLAs, performance budgets and a steady cadence of improvements driven
        by real product analytics &mdash; not opinions.
      </p>
      <ul>
        <li>Observability &amp; alerting (Sentry, Datadog, Grafana)</li>
        <li>Release management &amp; feature flags</li>
        <li>Roadmap &amp; iteration with measurable KPIs</li>
      </ul>
    </SplitSection>

    <StackRibbon
      label="Tech we ship with"
      items={[
        'TypeScript', 'React', 'Next.js', 'Node.js', 'NestJS',
        'Python', 'Go', 'PostgreSQL', 'MongoDB', 'Redis',
        'GraphQL', 'AWS', 'Docker', 'Kubernetes', 'Terraform',
      ]}
    />
  </ServicePage>
)

export default AppDevelopment
